import type {
  AuctionNavMenuGroup,
  AuctionNavMenuItem,
  GlobalHeaderNavData,
  SharedFooterData,
  SharedFooterLink,
  SharedFooterSocialPlatform,
} from '@b-stock/fe-core/bstock-react'

import type {
  CmsAuctionNav,
  CmsAuctionNavItem,
  CmsFooterLink,
  CmsMarketplaceFooter,
  CmsMarketplaceHeader,
} from '@/helpers/api/fetchPayload'

/**
 * Translates the CMS's field names into fe-core's.
 *
 * The two vocabularies differ on purpose — the CMS says `label`/`url`/`newTab` because that is
 * what reads well to an editor, and fe-core says `text`/`href`/`target` because that is what reads
 * well next to an anchor. Mapping here keeps each side free to name its own fields; putting the
 * translation in either one would pin its naming to the other's.
 *
 * Everything is defensive about missing values. Payload marks most of these required, but the
 * response is JSON over the wire and a half-filled document is the normal state while an editor is
 * still working — so a row missing its label or destination is dropped rather than rendered as an
 * empty link.
 */

function toFooterLink(link: CmsFooterLink | null): SharedFooterLink | null {
  const text = link?.label?.trim()
  const href = link?.url?.trim()
  if (!text || !href) return null

  return {
    text,
    href,
    ...(link?.dataTestId ? { dataTestId: link.dataTestId } : {}),
    // fe-core takes the two attributes rather than a flag, since that is what it puts on the
    // anchor. `rel` is not the editor's to choose: a new tab without it leaks the referrer.
    ...(link?.newTab ? { target: '_blank', rel: 'noreferrer' } : {}),
  }
}

function toFooterLinks(
  links: (CmsFooterLink | null)[] | null | undefined
): SharedFooterLink[] {
  return (links ?? [])
    .map(toFooterLink)
    .filter((l): l is SharedFooterLink => !!l)
}

const SOCIAL_PLATFORMS: SharedFooterSocialPlatform[] = [
  'facebook',
  'twitter',
  'linkedin',
]

function isSocialPlatform(
  value: string | null | undefined
): value is SharedFooterSocialPlatform {
  return !!value && (SOCIAL_PLATFORMS as string[]).includes(value)
}

/**
 * Absent sections are left off the returned object rather than set to an empty array, because
 * fe-core falls back per field: `undefined` means "use what you have bundled", while `[]` means
 * "render nothing". An editor who has not filled in the social row should get the bundled icons,
 * not a gap.
 */
export function toSharedFooterData(
  footer: CmsMarketplaceFooter | null
): SharedFooterData | undefined {
  if (!footer) return undefined

  const columns = (footer.navColumns ?? []).map((column) => ({
    heading: column?.heading?.trim() ?? '',
    links: toFooterLinks(column?.links),
  }))

  /**
   * The logo column is the one without a heading, not the one that happens to be first.
   *
   * The schema intends them in that order, but position is the editor's to change and a heading
   * is what actually decides how a column renders — headed ones sit beside the logo, the unheaded
   * one sits under it. Keyed on position, a reordered document would push a headed column under
   * the logo and drop its heading with nothing to say why.
   *
   * Only the first unheaded column is used. A second is dropped rather than merged: the footer
   * has one slot under the logo, and silently concatenating two columns of links into it would
   * be a stranger outcome than losing the ones the layout has nowhere to put.
   */
  const logoColumn = columns.find((column) => !column.heading)
  const navColumns = columns
    .filter((column) => column !== logoColumn)
    .filter((column) => column.heading && column.links.length > 0)
    .map((column) => ({ title: column.heading, links: column.links }))

  const socialLinks = (footer.socialLinks ?? [])
    .map((social) => ({ platform: social?.platform, url: social?.url?.trim() }))
    .filter(
      (
        social
      ): social is { platform: SharedFooterSocialPlatform; url: string } =>
        isSocialPlatform(social.platform) && !!social.url
    )

  const legalLinks = toFooterLinks(footer.legalLinks)
  const ctaPrimary = toFooterLink(footer.ctaPrimary ?? null)
  const ctaSecondary = toFooterLink(footer.ctaSecondary ?? null)
  const copyrightEntity = footer.copyrightText?.trim()

  return {
    ...(logoColumn?.links.length ? { logoColumnLinks: logoColumn.links } : {}),
    ...(navColumns.length ? { navColumns } : {}),
    ...(socialLinks.length ? { socialLinks } : {}),
    ...(copyrightEntity ? { copyrightEntity } : {}),
    ...(legalLinks.length ? { legalLinks } : {}),
    ...(ctaPrimary ? { ctaPrimary } : {}),
    ...(ctaSecondary ? { ctaSecondary } : {}),
  }
}

/**
 * Labels are omitted when blank, which is the normal state — fe-core translates those three words
 * and a supplied value replaces the translation in every locale.
 */
export function toGlobalHeaderNavData(
  header: CmsMarketplaceHeader | null
): GlobalHeaderNavData | undefined {
  if (!header) return undefined

  const supportLinks = (header.supportLinks ?? [])
    .map((link) => ({
      name: link?.name?.trim() ?? '',
      href: link?.href?.trim() ?? '',
      ...(link?.description?.trim()
        ? { description: link.description.trim() }
        : {}),
    }))
    .filter((link) => link.name && link.href)

  const forBuyerLink = header.forBuyerLink?.trim()
  const forSellerLink = header.forSellerLink?.trim()
  const supportLabel = header.supportLabel?.trim()
  const forBuyerLabel = header.forBuyerLabel?.trim()
  const forSellerLabel = header.forSellerLabel?.trim()

  return {
    ...(supportLinks.length ? { supportLinks } : {}),
    ...(supportLabel ? { supportLabel } : {}),
    ...(forBuyerLink ? { forBuyerLink } : {}),
    ...(forBuyerLabel ? { forBuyerLabel } : {}),
    ...(forSellerLink ? { forSellerLink } : {}),
    ...(forSellerLabel ? { forSellerLabel } : {}),
  }
}

/**
 * The second header row, as `GlobalHeaderAuctionNavigation` consumes it.
 *
 * Sellers carries a label and no list on purpose — that dropdown is built from the seller service
 * at render, and a hand-typed copy would be wrong the day a seller is added.
 *
 * Each list is `undefined` when the CMS has nothing for it, so the caller falls back per section:
 * an editor who has filled in categories but not regions gets CMS categories and the committed
 * regions, rather than losing a dropdown.
 */
export type MarketplaceAuctionNav = {
  categoriesLabel?: string
  categories?: AuctionNavMenuItem[]
  sellersLabel?: string
  conditionsLabel?: string
  conditions?: AuctionNavMenuItem[]
  regionsLabel?: string
  regions?: AuctionNavMenuGroup[]
}

function toNavItem(item: CmsAuctionNavItem | null): AuctionNavMenuItem | null {
  const name = item?.name?.trim()
  const href = item?.href?.trim()
  if (!name || !href) return null
  return { name, href }
}

function toNavItems(
  items: (CmsAuctionNavItem | null)[] | null | undefined
): AuctionNavMenuItem[] {
  return (items ?? [])
    .map(toNavItem)
    .filter((item): item is AuctionNavMenuItem => !!item)
}

export function toAuctionNavData(
  header: CmsMarketplaceHeader | null
): MarketplaceAuctionNav | undefined {
  const nav: CmsAuctionNav | null | undefined = header?.auctionNav
  if (!nav) return undefined

  const categories = toNavItems(nav.categories)
  const conditions = toNavItems(nav.conditions)

  const regions = (nav.regions ?? [])
    .map((group): AuctionNavMenuGroup | null => {
      const heading = group?.header?.trim()
      const items = toNavItems(group?.items)
      // A heading with no links renders as a column of nothing.
      if (!heading || items.length === 0) return null
      const footer = toNavItem(group?.footer ?? null)
      return {
        header: heading,
        items,
        ...(footer ? { footer } : {}),
      }
    })
    .filter((group): group is AuctionNavMenuGroup => !!group)

  const categoriesLabel = nav.categoriesLabel?.trim()
  const sellersLabel = nav.sellersLabel?.trim()
  const conditionsLabel = nav.conditionsLabel?.trim()
  const regionsLabel = nav.regionsLabel?.trim()

  return {
    ...(categories.length ? { categories } : {}),
    ...(conditions.length ? { conditions } : {}),
    ...(regions.length ? { regions } : {}),
    ...(categoriesLabel ? { categoriesLabel } : {}),
    ...(sellersLabel ? { sellersLabel } : {}),
    ...(conditionsLabel ? { conditionsLabel } : {}),
    ...(regionsLabel ? { regionsLabel } : {}),
  }
}
